import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Share2, Mail } from "lucide-react";
import { api } from "@/lib/api";

const WaIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 004.76 1.21h.004c5.46 0 9.9-4.44 9.9-9.9 0-2.64-1.03-5.13-2.9-7A9.82 9.82 0 0012.04 2zm5.8 14.06c-.24.68-1.42 1.32-1.95 1.36-.5.05-1.12.24-3.66-.77-3.08-1.22-5.03-4.38-5.18-4.58-.15-.2-1.24-1.65-1.24-3.15s.79-2.24 1.07-2.55c.28-.3.6-.38.8-.38.2 0 .4.002.57.01.18.008.43-.07.67.51.24.6.82 2.07.9 2.22.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.3.76 1.25 1.63 2.02 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.64-.07.17-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.28.1 1.75.83 2.05.98.3.15.5.22.57.35.07.13.07.72-.17 1.4z"/></svg>
);

const SB_T = {
  fr: { card: "Carte à partager", wa: "WhatsApp", email: "E-mail", error: "Le partage a échoué." },
  en: { card: "Shareable card", wa: "WhatsApp", email: "Email", error: "Sharing failed." },
  es: { card: "Tarjeta para compartir", wa: "WhatsApp", email: "Correo", error: "Falló al compartir." },
  nl: { card: "Deelbare kaart", wa: "WhatsApp", email: "E-mail", error: "Delen mislukt." },
};

// Barre de partage unifiée : Carte (PNG) · WhatsApp · E-mail.
export function ShareBar({
  lang = "fr",
  endpoint = "/expedition/card",
  payload,
  summary,
  title = "Cocoon Mind",
  subject,
  filename = "cocoon.png",
  testPrefix = "share",
  className = "",
}) {
  const [sharing, setSharing] = useState(false);
  const t = SB_T[(lang || "fr").slice(0, 2)] || SB_T.fr;

  const fetchBlob = async () => {
    const { data } = await api.post(endpoint, payload, { responseType: "blob" });
    return data;
  };

  const shareCard = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const blob = await fetchBlob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(t.error);
    } finally {
      setSharing(false);
    }
  };

  const shareWhatsApp = async () => {
    if (sharing) return;
    setSharing(true);
    const waFallback = () => window.open(`https://wa.me/?text=${encodeURIComponent(summary || title)}`, "_blank", "noopener,noreferrer");
    try {
      const blob = await fetchBlob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: summary, title });
      } else {
        waFallback();
      }
    } catch (e) {
      if (e?.name !== "AbortError") waFallback();
    } finally {
      setSharing(false);
    }
  };

  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(subject || title)}&body=${encodeURIComponent(summary || title)}`;
  };

  return (
    <div className={`flex flex-wrap items-center justify-center gap-3 ${className}`} data-testid={`${testPrefix}-bar`}>
      <button onClick={shareCard} disabled={sharing} data-testid={`${testPrefix}-card`}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-primary/90 border border-primary/30 hover:bg-primary/10 transition-colors disabled:opacity-50">
        {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {t.card}
      </button>
      <button onClick={shareWhatsApp} disabled={sharing} data-testid={`${testPrefix}-wa`}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/10 transition-colors disabled:opacity-50">
        {sharing ? <Loader2 size={15} className="animate-spin" /> : <WaIcon />} {t.wa}
      </button>
      <button onClick={shareEmail} data-testid={`${testPrefix}-email`}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/70 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors">
        <Mail size={15} /> {t.email}
      </button>
    </div>
  );
}
